import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { registerGsap, ScrollTrigger } from '@/lib/gsap'

interface AnimationProviderProps {
    children: ReactNode
}

/**
 * Boots the GSAP runtime once on the client and keeps ScrollTrigger in sync
 * across client-side navigations. Individual sections register their own
 * timelines; this only owns global lifecycle concerns.
 */
const AnimationProvider: FC<AnimationProviderProps> = ({ children }) => {
    const router = useRouter()

    useEffect(() => {
        registerGsap()

        // Recalculate trigger positions once fonts/images have settled.
        const refresh = () => ScrollTrigger.refresh()
        window.addEventListener('load', refresh)
        const timeout = window.setTimeout(refresh, 600)

        return () => {
            window.removeEventListener('load', refresh)
            window.clearTimeout(timeout)
        }
    }, [])

    useEffect(() => {
        const handleRouteChange = () => {
            // Kill stale triggers from the previous page, then recalc.
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
            window.scrollTo(0, 0)
            requestAnimationFrame(() => ScrollTrigger.refresh())
        }

        router.events.on('routeChangeComplete', handleRouteChange)
        return () => router.events.off('routeChangeComplete', handleRouteChange)
    }, [router.events])

    return <>{children}</>
}

export default AnimationProvider
